<template>
    <vx-box color="info" class="p-10 bg-slate-50 space-y-6">
        <div class="flex items-center justify-between">
            <div>
                <div class="text-slate-800 font-extrabold text-lg lg:text-xl mb-1">Enable Group Access</div>
                <div class="text-sm text-slate-700">Groups will only be able to message preferred contacts.</div>
            </div>
            <vx-toggle v-model="enableGroupAccess" size="lg"></vx-toggle>
        </div>

        <vx-alert v-if="!enableGroupAccess" :closeable="false">
            Group Access is disabled. This external list is not shared with any groups.
        </vx-alert>

        <div v-if="enableGroupAccess" class="space-y-6">
            <vx-tabs v-model="tab" @update:modelValue="tabChange()" size="lg">
                <vx-tab value="filters">Use Filters</vx-tab>
                <vx-tab value="collection">Choose a Collection</vx-tab>
                <vx-tab value="all">All Groups</vx-tab>
            </vx-tabs>

            <div v-if="tab === 'filters'">
                <group-collection-filters v-model="groupFilters"></group-collection-filters>
                <div
                    v-if="hasEmptyFilters"
                    class="mt-4 bg-sky-100 border border-sky-200 rounded-lg py-4 px-5 flex items-start gap-3"
                >
                    <font-awesome-icon
                        widthAuto
                        :icon="faCircleInfo"
                        class="text-sky-600 mt-0.5 shrink-0"
                    ></font-awesome-icon>
                    <div class="text-slate-800 text-sm">
                        No filters set. All Groups will have access to this external list.
                    </div>
                </div>
                <div v-if="props.error" class="relative pt-4 text-xs text-rose-800">
                    {{ props.error }}
                </div>
            </div>
            <div v-if="tab === 'collection'">
                <select-group-collection size="lg" v-model="groupCollectionId"></select-group-collection>
                <vx-alert v-if="!groupCollectionId" class="mt-4" :closeable="false">
                    No collection selected. All Groups will have access to this external list.
                </vx-alert>
            </div>
            <div v-if="tab === 'all'">
                <vx-alert :closeable="false"> All Groups will have access to this external list. </vx-alert>
            </div>
        </div>
    </vx-box>
</template>

<script lang="ts" setup>
import { VxAlert, VxBox, VxToggle, VxTabs, VxTab } from '@voxie/frontend-components';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, onMounted, ref, watch } from 'vue';
import { randomString } from '~/utils/string';
import SelectGroupCollection from '~/components/general/SelectGroupCollection.vue';
import GroupCollectionFilters from '~/components/settings/teams/group-collections/form/GroupCollectionFilters.vue';

interface GroupFilter {
    id: string;
    attribute: string;
    operator: string;
    key: string;
    values: string[];
}

interface Props {
    externalListId?: number;
    error?: string;
}

const props = defineProps<Props>();

const groupFilters = defineModel<GroupFilter[]>('groupFilters', {
    default: () => [],
});

const groupCollectionId = defineModel<number | null>('groupCollectionId', {
    default: null,
});

const enableGroupAccess = defineModel<boolean>('enableGroupAccess', {
    default: false,
});

type TabValue = 'filters' | 'collection' | 'all';
const tab = ref<TabValue>('filters');

const hasEmptyFilters = computed<boolean>(() => {
    if (groupFilters.value.length === 0) {
        return true;
    }
    return groupFilters.value.every((filter) => !filter.attribute);
});

onMounted(() => {
    if (groupCollectionId.value) {
        tab.value = 'collection';
    } else if (groupFilters.value.length > 0) {
        tab.value = 'filters';
    } else if (enableGroupAccess.value) {
        tab.value = 'all';
    } else {
        tab.value = 'filters';
    }

    if (enableGroupAccess.value && tab.value === 'filters' && groupFilters.value.length === 0) {
        addFilter();
    }
});

watch(tab, (newTab) => {
    if (enableGroupAccess.value && newTab === 'filters' && groupFilters.value.length === 0) {
        addFilter();
    }
});

watch(enableGroupAccess, (enabled, wasEnabled) => {
    if (!enabled) {
        groupFilters.value = [];
        groupCollectionId.value = null;
        tab.value = 'all';
    } else if (!wasEnabled && enabled) {
        if (tab.value === 'all' || !groupFilters.value.length) {
            tab.value = 'filters';
            if (groupFilters.value.length === 0) {
                addFilter();
            }
        }
    }
});

const addFilter = (): void => {
    groupFilters.value.push({
        id: randomString(),
        attribute: '',
        operator: '',
        key: '',
        values: [],
    });
};

const tabChange = (): void => {
    groupFilters.value = [];
    groupCollectionId.value = null;

    if (tab.value === 'filters') {
        addFilter();
    }
};
</script>
